import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { toast } from 'sonner';
import { V3_LAYOUT } from './v3Config';
import { getPageZoom, getUiBoost } from '../utils/desktopZoomFix';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAR_STATES = ['open', 'collapsed'];
const THEMES = ['dark', 'light'];

// Each edge bar has two settings that the person chooses (in My preferences, and with the two buttons at the foot of each bar):
//   default_state: how it starts, 'open' or 'collapsed' (its icon strip). Until chosen, the left bar starts open and the right bar starts minimised.
//   mode: how it works.
//     'pinned'  : open, it is part of the page (it moves the content across) and it stays open until the person folds it.
//     'auto'    : it opens as part of the page too (it moves the content across), but it folds again by itself when the person clicks anywhere outside it.
//     'overlay' : it is always an icon strip, and opens over the content (it does not move it) and closes when the person clicks away.
//   Until chosen, the left bar is pinned and the right bar is auto.
// A page, a window or a small screen can still override this while it is showing, and the arrow on a bar opens or folds it for the visit (that is not saved).
const BAR_MODES = ['pinned', 'auto', 'overlay'];
const makeBar = (bar) => ({ default_state: bar === 'right' ? 'collapsed' : 'open', mode: bar === 'right' ? 'auto' : 'pinned' });

const DEFAULT_PREFS = { theme: 'light', left: makeBar('left'), right: makeBar('right') };

const cleanBar = (raw, bar) => {
  const result = makeBar(bar);
  if (!raw || typeof raw !== 'object') return result;
  if (BAR_STATES.includes(raw.default_state)) result.default_state = raw.default_state;
  if (BAR_MODES.includes(raw.mode)) result.mode = raw.mode;
  else if (raw.docked === false) result.mode = 'overlay'; // saved by the earlier version: a bar that had been undocked stays over the page
  return result;
};

const cleanPrefs = (raw) => ({
  theme: raw && THEMES.includes(raw.theme) ? raw.theme : 'light',
  left: cleanBar(raw && raw.left, 'left'),
  right: cleanBar(raw && raw.right, 'right'),
});

const storageKey = (userId) => `c30_v3_prefs_${userId || 'anon'}`;

const readLocal = (userId) => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return raw ? cleanPrefs(JSON.parse(raw)) : null;
  } catch (_) { return null; }
};

const isImpersonating = () => typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('_impersonated_as');

const V3PrefsContext = createContext(null);

export const useV3Prefs = () => {
  const value = useContext(V3PrefsContext);
  if (!value) throw new Error('useV3Prefs must be used inside the V3 shell');
  return value;
};

export const V3PrefsProvider = ({ user, children }) => {
  const userId = user?.id;
  const [prefs, setPrefs] = useState(() => readLocal(userId) || DEFAULT_PREFS);
  const [width, setWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1920));
  // Goes up whenever the page zoom changes (small screens), so the minimised bars are measured again at their larger size.
  const [zoomTick, setZoomTick] = useState(0);
  const [overlay, setOverlay] = useState({ left: false, right: false });
  // Which tab the right bar is showing ('recents' or 'tasks'). Not saved: it is just where the person is looking.
  const [rightTab, setRightTab] = useState('recents');
  // A page can ask for both bars to stay as icon strips that open over the page (Transport does, to keep room for its map).
  const [compactBars, setCompactBars] = useState(false);
  // How much width (in the page's own pixels) the open window(s) want. 0 means no window is open. The shell sets it; see `modes` below.
  const [modalNeed, setModalNeed] = useState(0);
  // What the arrow on each bar has done during this visit: 'open', 'collapsed', or null (use the person's default). Not saved.
  const [visitState, setVisitState] = useState({ left: null, right: null });
  // The person's nickname and photo (from the user circle on the left bar), and whether the My preferences window is open.
  const [profile, setProfile] = useState(() => ({ nickname: user?.nickname || '', photoId: user?.photo_id || '' }));
  const [prefsOpen, setPrefsOpen] = useState(false);
  // How the last attempt to save the person's choices went: 'idle', 'saving', 'saved', 'failed' (detail says why) or 'blocked' (viewing as another person: nothing goes to the server).
  const [saveStatus, setSaveStatus] = useState({ state: 'idle', detail: '' });
  const saveTimer = useRef(null);
  const loadedFor = useRef(null);
  const pending = useRef(null);    // the choices waiting to be sent to the server
  const touched = useRef(false);   // something was changed before the server copy arrived
  const flushRef = useRef(() => {});

  useEffect(() => {
    setProfile({ nickname: user?.nickname || '', photoId: user?.photo_id || '' });
  }, [userId, user?.nickname, user?.photo_id]);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  useEffect(() => {
    const onZoom = () => setZoomTick((tick) => tick + 1);
    window.addEventListener('c30:page-zoom', onZoom);
    return () => window.removeEventListener('c30:page-zoom', onZoom);
  }, []);
  // Load this person's saved choices from the server once. Until then the copy kept in this browser is used.
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    api.get(`${API}/dashboard-v3/prefs`)
      .then((res) => {
        if (cancelled) return;
        loadedFor.current = userId;
        // Something was changed before the server copy arrived: that newer choice wins and is sent now.
        if (touched.current) { touched.current = false; flushRef.current(); return; }
        setPrefs(cleanPrefs(res.data));
      })
      .catch(() => {
        // The browser copy keeps working, and what is changed from here on is still saved.
        if (cancelled) return;
        loadedFor.current = userId;
        if (touched.current) { touched.current = false; flushRef.current(); }
      });
    return () => { cancelled = true; };
  }, [userId]);

  // Sends the waiting choices to the server. A failure is shown (a message, and the line in My preferences): it is never swallowed.
  const flush = useCallback(async () => {
    const payload = pending.current;
    if (!payload || isImpersonating() || loadedFor.current !== userId) return;
    pending.current = null;
    setSaveStatus({ state: 'saving', detail: '' });
    try {
      await api.put(`${API}/dashboard-v3/prefs`, payload);
      setSaveStatus({ state: 'saved', detail: '' });
    } catch (error) {
      const response = error && error.response;
      const reason = (response && response.data && typeof response.data.detail === 'string' && response.data.detail) || (error && error.message) || 'no reply from the server';
      const detail = `${response && response.status ? `error ${response.status}: ` : ''}${reason}`;
      setSaveStatus({ state: 'failed', detail });
      toast.error(`Your side bar choice could not be saved (${detail}). It is kept on this device.`);
    }
  }, [userId]);
  flushRef.current = flush;

  // Leaving the page (a reload, closing the tab, switching away) sends anything still waiting at once, so a choice made a moment ago is never lost.
  useEffect(() => {
    const sendNow = () => {
      const payload = pending.current;
      if (!payload || isImpersonating() || loadedFor.current !== userId) return;
      pending.current = null;
      clearTimeout(saveTimer.current);
      try {
        const token = localStorage.getItem('token');
        fetch(`${API}/dashboard-v3/prefs`, { method: 'PUT', keepalive: true, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(payload) });
      } catch (_) { /* saved on this device regardless */ }
    };
    const onHide = () => { if (document.visibilityState === 'hidden') sendNow(); };
    window.addEventListener('pagehide', sendNow);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', sendNow);
      document.removeEventListener('visibilitychange', onHide);
      clearTimeout(saveTimer.current);
      sendNow();
    };
  }, [userId]);

  const persist = useCallback((next) => {
    try { localStorage.setItem(storageKey(userId), JSON.stringify(next)); } catch (_) { /* private mode */ }
    // While a super admin is viewing as another person the choices stay on this device (they are never written over that person's own).
    if (isImpersonating()) { Promise.resolve().then(() => setSaveStatus({ state: 'blocked', detail: '' })); return; }
    pending.current = next;
    // Before the server copy has arrived the choice is held, and sent the moment it has.
    if (loadedFor.current !== userId) { touched.current = true; return; }
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flushRef.current(), 400);
  }, [userId]);

  const update = useCallback((change) => {
    setPrefs((previous) => {
      const next = change(previous);
      persist(next);
      return next;
    });
  }, [persist]);

  const setTheme = useCallback((theme) => {
    if (!THEMES.includes(theme)) return;
    update((previous) => ({ ...previous, theme }));
  }, [update]);

  // The arrow on a bar: opens or folds it for this visit only.
  const setBarState = useCallback((bar, state) => {
    if (!BAR_STATES.includes(state)) return;
    setVisitState((previous) => ({ ...previous, [bar]: state }));
  }, []);

  // My preferences: whether a bar is open or collapsed by default. Saved, and it takes effect straight away.
  const setDefaultState = useCallback((bar, state) => {
    if (!BAR_STATES.includes(state)) return;
    update((previous) => ({ ...previous, [bar]: { ...previous[bar], default_state: state } }));
    setVisitState((previous) => ({ ...previous, [bar]: null }));
  }, [update]);

  // How a bar works (pinned, auto, overlay). Saved. A bar that is open when its button is pressed stays open (as pinned or auto); one made 'overlay' folds to its strip.
  const setBarMode = useCallback((bar, mode) => {
    if (!BAR_MODES.includes(mode)) return;
    update((previous) => ({ ...previous, [bar]: { ...previous[bar], mode } }));
    setVisitState((previous) => ({ ...previous, [bar]: mode === 'overlay' ? null : 'open' }));
    setOverlay({ left: false, right: false });
  }, [update]);

  const closeOverlay = useCallback(() => setOverlay({ left: false, right: false }), []);

  // How each bar is drawn right now: 'open' (full, part of the page), 'strip' (icons only) or 'hidden' (behind an edge handle).
  const baseModes = useMemo(() => {
    const compute = (bar) => {
      const settings = prefs[bar];
      if (width < V3_LAYOUT.tabletBreakpoint) return 'hidden';
      // On a small screen (an iPad) the right bar is always an icon strip that opens over the page: it is never pinned or docked.
      if (bar === 'right' && width < V3_LAYOUT.smallBreakpoint) return 'strip';
      if (compactBars) return 'strip';
      if (settings.mode === 'overlay') return 'strip';
      return (visitState[bar] || settings.default_state) === 'collapsed' ? 'strip' : 'open';
    };
    return { left: compute('left'), right: compute('right') };
  }, [prefs, width, compactBars, visitState]);

  // While a window (a modal) is open it says how much width it wants. If the room between the two bars is not enough, the bars that are
  // open are pushed shut to icon strips, the left one first and then the right one, so the window can use more of the content area.
  // Nothing is saved: when the window closes, or no longer needs the room, the bars spring back to the layout the person chose.
  const modalOpen = modalNeed > 0;
  const modes = useMemo(() => {
    if (!modalOpen || width < V3_LAYOUT.tabletBreakpoint) return baseModes;
    const pageWidth = width / (getPageZoom() || 1);
    const barPx = (bar, mode) => {
      if (mode === 'open') return bar === 'left' ? V3_LAYOUT.nav : (baseModes.left === 'open' ? V3_LAYOUT.rail : V3_LAYOUT.rail * 2);
      if (mode === 'strip') return Math.round((bar === 'left' ? V3_LAYOUT.navStrip : V3_LAYOUT.railStrip) * getUiBoost());
      return 0;
    };
    // The room for the window: the width between the bars, less the gap beside each bar and the window's own 12px margin on both sides.
    const room = (left, right) => pageWidth - barPx('left', left) - barPx('right', right) - 2 * V3_LAYOUT.gap - 24;
    const next = { ...baseModes };
    if (room(next.left, next.right) < modalNeed && next.left === 'open') next.left = 'strip';
    if (room(next.left, next.right) < modalNeed && next.right === 'open') next.right = 'strip';
    return next;
  }, [baseModes, modalOpen, modalNeed, width, zoomTick]);

  // A minimised bar is wider on a small screen (see getUiBoost): its icons are drawn larger and it has the room for them.
  const widths = useMemo(() => {
    const boost = getUiBoost();
    // The open right bar is twice as wide whenever the left bar is not open beside it (minimised or hidden), and when it opens over the page (that is in v3.css).
    // With both bars open it stays its usual width. It is judged on the person's own choice for the left bar, not on a window pushing it shut.
    const rightOpen = baseModes.left === 'open' ? V3_LAYOUT.rail : V3_LAYOUT.rail * 2;
    return {
      left: modes.left === 'open' ? V3_LAYOUT.nav : (modes.left === 'strip' ? Math.round(V3_LAYOUT.navStrip * boost) : 0),
      right: modes.right === 'open' ? rightOpen : (modes.right === 'strip' ? Math.round(V3_LAYOUT.railStrip * boost) : 0),
    };
  }, [modes, baseModes, zoomTick]);

  // Whether the bar could be docked beside the open window(s) without squeezing them: the same sums as `modes` above, with this bar open.
  const roomToDock = useCallback((bar) => {
    const pageWidth = width / (getPageZoom() || 1);
    const leftMode = bar === 'left' ? 'open' : modes.left;
    const rightMode = bar === 'right' ? 'open' : modes.right;
    const px = (which, mode) => {
      if (mode === 'open') return which === 'left' ? V3_LAYOUT.nav : (leftMode === 'open' ? V3_LAYOUT.rail : V3_LAYOUT.rail * 2);
      if (mode === 'strip') return Math.round((which === 'left' ? V3_LAYOUT.navStrip : V3_LAYOUT.railStrip) * getUiBoost());
      return 0;
    };
    return pageWidth - px('left', leftMode) - px('right', rightMode) - 2 * V3_LAYOUT.gap - 24 >= modalNeed;
  }, [width, modes, modalNeed]);

  // The arrow on each bar.
  const toggleBar = useCallback((bar) => {
    if (overlay[bar]) { setOverlay((previous) => ({ ...previous, [bar]: false })); return; }
    // While a page keeps the bars compact, or a window is open that has no room beside the bar, or the bar works over the page, opening it slides it over the page instead of docking it.
    if (compactBars || (modalOpen && modes[bar] !== 'open' && !roomToDock(bar)) || prefs[bar].mode === 'overlay' || (bar === 'right' && width < V3_LAYOUT.smallBreakpoint)) { setOverlay((previous) => ({ ...previous, [bar]: true })); return; }
    // A pinned bar remembers what its arrow does: minimise it and it starts minimised next time (and opens docked, as part of the page); open it and it starts open.
    // 'Pinned' means 'keep it the way I leave it'. A bar that closes by itself, and one that works over the page, are not saved (that would fight their own setting).
    const remember = (state) => { if (prefs[bar].mode === 'pinned') setDefaultState(bar, state); else setBarState(bar, state); };
    if (modes[bar] === 'open') { remember('collapsed'); return; }
    if (width >= V3_LAYOUT.tabletBreakpoint) remember('open');
    else setOverlay((previous) => ({ ...previous, [bar]: true }));
  }, [overlay, prefs, modes, width, compactBars, modalOpen, roomToDock, setBarState, setDefaultState]);

  // A bar that works in 'auto' mode folds by itself when the person clicks anywhere outside it.
  useEffect(() => {
    const watched = ['left', 'right'].filter((bar) => prefs[bar].mode === 'auto' && modes[bar] === 'open' && !compactBars && !overlay[bar]);
    if (!watched.length) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      watched.forEach((bar) => {
        if (target.closest(`.v3-bar--${bar}`)) return;
        setVisitState((previous) => (previous[bar] === 'collapsed' ? previous : { ...previous, [bar]: 'collapsed' }));
      });
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [prefs, modes, compactBars, overlay]);

  const value = useMemo(() => ({
    prefs,
    theme: prefs.theme,
    width,
    modes,
    widths,
    overlay,
    canPin: width >= V3_LAYOUT.tabletBreakpoint && width < V3_LAYOUT.laptopBreakpoint,
    // The right bar cannot be pinned or set to close by itself on a small screen: it always opens over the page.
    rightCanPin: width >= V3_LAYOUT.smallBreakpoint,
    smallScreen: width < V3_LAYOUT.smallBreakpoint,
    defaults: { left: prefs.left.default_state, right: prefs.right.default_state },
    barMode: { left: prefs.left.mode, right: prefs.right.mode },
    // Old names, kept so anything still using them keeps working.
    docked: { left: prefs.left.mode !== 'overlay', right: prefs.right.mode !== 'overlay' },
    pinned: { left: prefs.left.mode === 'pinned', right: prefs.right.mode === 'pinned' },
    setDocked: (bar, docked) => setBarMode(bar, docked ? 'pinned' : 'overlay'),
    setPinned: (bar, pinned) => setBarMode(bar, pinned ? 'pinned' : 'overlay'),
    rightTab,
    setRightTab,
    compactBars,
    setCompactBars,
    modalOpen,
    setModalNeed,
    profile,
    setProfile,
    prefsOpen,
    setPrefsOpen,
    saveStatus,
    setTheme,
    setBarState,
    setDefaultState,
    setBarMode,
    toggleBar,
    closeOverlay,
  }), [prefs, width, modes, widths, overlay, rightTab, compactBars, modalOpen, profile, prefsOpen, saveStatus, setTheme, setBarState, setDefaultState, setBarMode, toggleBar, closeOverlay]);

  return <V3PrefsContext.Provider value={value}>{children}</V3PrefsContext.Provider>;
};

export default V3PrefsContext;
